import React, { useState, useEffect, useMemo } from "react";
import { UK_COUNTRY_CODE, AU_COUNTRY_CODE, getCountry } from "../../../helpers/urls.helper";
import { usePrefersReducedMotion } from "../../../helpers/hooks";
import Image from "../../atoms/Image";

import featuredLogoHalliday from "../../../assets/images/featured/halliday.png";
import featuredLogoHallidayWebp from "../../../assets/images/featured/halliday.webp";
import featuredLogoGourmetTraveller from "../../../assets/images/featured/gourmet-traveller.png";
import featuredLogoGourmetTravellerWebp from "../../../assets/images/featured/gourmet-traveller.webp";
import featuredLogoPedestrian from "../../../assets/images/featured/pedestrian.png";
import featuredLogoPedestrianWebp from "../../../assets/images/featured/pedestrian.webp";
import featuredLogoTheAge from "../../../assets/images/featured/the-age.png";
import featuredLogoTheAgeWebp from "../../../assets/images/featured/the-age.webp";
import featuredLogoSydneyMorningHerald from "../../../assets/images/featured/sydney-morning-herald.png";
import featuredLogoSydneyMorningHeraldWebp from "../../../assets/images/featured/sydney-morning-herald.webp";
import featuredLogoUrbanList from "../../../assets/images/featured/urbanlist.png";
import featuredLogoUrbanListWebp from "../../../assets/images/featured/urbanlist.webp";
import featuredLogoRetailDrinksAustralia from "../../../assets/images/featured/retail-drinks-australia.png";
import featuredLogoRetailDrinksAustraliaWebp from "../../../assets/images/featured/retail-drinks-australia.webp";
import featuredLogoGoodFood from "../../../assets/images/featured/goodfood.png";
import featuredLogoGoodFoodWebp from "../../../assets/images/featured/goodfood.webp";
import featuredLogoBroadsheet from "../../../assets/images/featured/broadsheet.png";
import featuredLogoBroadsheetWebp from "../../../assets/images/featured/broadsheet.webp";
import featuredLogoGQ from "../../../assets/images/featured/gq.png";
import featuredLogoGQWebp from "../../../assets/images/featured/gq.webp";
import featuredLogoVogue from "../../../assets/images/featured/vogue.png";
import featuredLogoVogueWebp from "../../../assets/images/featured/vogue.webp";
import featuredLogoElle from "../../../assets/images/featured/elle.png";
import featuredLogoElleWebp from "../../../assets/images/featured/elle.webp";
import featuredHello from "../../../assets/images/featured/hello.png";
import featuredHelloWebp from "../../../assets/images/featured/hello.webp";

import { BrandMarquee, BrandMarqueeTrack, BrandGalleryHolder } from "./style";

const auLogos = [
  {
    alt: "Sydney Morning Herald",
    src: featuredLogoSydneyMorningHerald,
    webpSrc: featuredLogoSydneyMorningHeraldWebp,
    maxHeight: 25,
    maxWidth: 233,
  },
  { alt: "Vogue", src: featuredLogoVogue, webpSrc: featuredLogoVogueWebp, maxHeight: 37, maxWidth: 139 },
  { alt: "The Age", src: featuredLogoTheAge, webpSrc: featuredLogoTheAgeWebp, maxHeight: 40, maxWidth: 187 },
  {
    alt: "Broadsheet",
    src: featuredLogoBroadsheet,
    webpSrc: featuredLogoBroadsheetWebp,
    maxHeight: 54,
    maxWidth: 170,
  },
  {
    alt: "Good Food",
    src: featuredLogoGoodFood,
    webpSrc: featuredLogoGoodFoodWebp,
    maxHeight: 36,
    maxWidth: 167,
  },
  {
    alt: "Urban List",
    src: featuredLogoUrbanList,
    webpSrc: featuredLogoUrbanListWebp,
    maxHeight: 45,
    maxWidth: 108,
  },
  {
    alt: "Retail Drinks Australia",
    src: featuredLogoRetailDrinksAustralia,
    webpSrc: featuredLogoRetailDrinksAustraliaWebp,
    maxHeight: 48,
    maxWidth: 215,
  },
  {
    alt: "Halliday",
    src: featuredLogoHalliday,
    webpSrc: featuredLogoHallidayWebp,
    maxHeight: 100,
    maxWidth: 141,
  },
  {
    alt: "Gourmet Traveller",
    src: featuredLogoGourmetTraveller,
    webpSrc: featuredLogoGourmetTravellerWebp,
    maxHeight: 36,
    maxWidth: 136,
  },
  {
    alt: "Pedestrian",
    src: featuredLogoPedestrian,
    webpSrc: featuredLogoPedestrianWebp,
    maxHeight: 37,
    maxWidth: 222,
  },
];

const ukLogos = [
  { alt: "Vogue", src: featuredLogoVogue, webpSrc: featuredLogoVogueWebp, maxHeight: 37, maxWidth: 139 },
  { alt: "GQ", src: featuredLogoGQ, webpSrc: featuredLogoGQWebp, maxHeight: 45, maxWidth: 100 },
  {
    alt: "Broadsheet",
    src: featuredLogoBroadsheet,
    webpSrc: featuredLogoBroadsheetWebp,
    maxHeight: 54,
    maxWidth: 170,
  },
  {
    alt: "Good Food",
    src: featuredLogoGoodFood,
    webpSrc: featuredLogoGoodFoodWebp,
    maxHeight: 36,
    maxWidth: 167,
  },
  { alt: "Elle", src: featuredLogoElle, webpSrc: featuredLogoElleWebp, maxHeight: 40, maxWidth: 167 },
  {
    alt: "Gourmet Traveller",
    src: featuredLogoGourmetTraveller,
    webpSrc: featuredLogoGourmetTravellerWebp,
    maxHeight: 36,
    maxWidth: 136,
  },
  { alt: "Hello", src: featuredHello, webpSrc: featuredHelloWebp, maxHeight: 40, maxWidth: 160 },
];

const renderLogos = (logos, prefix = "") =>
  logos.map((logo) => (
    <BrandGalleryHolder
      key={`${prefix}${logo.alt}`}
      maxHeight={logo.maxHeight}
      maxWidth={logo.maxWidth}
      aria-hidden={prefix ? true : undefined}
    >
      <Image
        alt={logo.alt}
        src={logo.src}
        webpSrc={logo.webpSrc}
        height={logo.maxHeight}
        width={logo.maxWidth}
        loading="lazy"
        decoding="async"
      />
    </BrandGalleryHolder>
  ));

const ITEM_HORIZONTAL_PADDING_PX = 60;
const INTERVAL_MS = 3000;
const TRANSITION_MS = 500;

/**
 * Auto-advancing brand-logo marquee.
 *
 * Same cloned-first-set loop pattern as the press-quote rotator: render the
 * logos twice, translate the track by precomputed pixel offsets (logos have
 * variable widths so we sum `maxWidth + ITEM_HORIZONTAL_PADDING_PX` per
 * item), and on `transitionend` past the last original logo snap the track
 * back to offset 0 with no transition. The double-rAF commits that snap
 * before transitions resume.
 *
 * Pauses on hover. Skips auto-advance under `prefers-reduced-motion: reduce`
 * and for single-item lists.
 */
const Marquee = ({ logos }) => {
  const [index, setIndex] = useState(0);
  const [withTransition, setWithTransition] = useState(true);
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Pixel offset for each index — sum of widths of preceding holders.
  // Holder width is `maxWidth + 60` (padding on both sides) per the styled rules.
  const offsets = useMemo(() => {
    const arr = [0];
    logos.forEach((l) => arr.push(arr[arr.length - 1] + l.maxWidth + ITEM_HORIZONTAL_PADDING_PX));
    return arr;
  }, [logos]);

  useEffect(() => {
    if (prefersReducedMotion || paused || logos.length <= 1) return undefined;
    const id = setInterval(() => {
      setWithTransition(true);
      setIndex((i) => i + 1);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, [prefersReducedMotion, paused, logos.length]);

  // After sliding past the last original into the cloned set, snap
  // back to index 0 with no transition for a seamless loop.
  const handleTransitionEnd = () => {
    if (index >= logos.length) {
      setWithTransition(false);
      setIndex(0);
    }
  };

  useEffect(() => {
    if (withTransition) return undefined;
    let id2;
    const id1 = requestAnimationFrame(() => {
      id2 = requestAnimationFrame(() => setWithTransition(true));
    });
    return () => {
      cancelAnimationFrame(id1);
      if (id2) cancelAnimationFrame(id2);
    };
  }, [withTransition]);

  if (logos.length === 0) return null;

  return (
    <BrandMarquee
      aria-label="As featured in"
      aria-roledescription="carousel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <BrandMarqueeTrack
        $withTransition={withTransition}
        $transitionMs={TRANSITION_MS}
        style={{ transform: `translateX(-${offsets[index]}px)` }}
        onTransitionEnd={handleTransitionEnd}
      >
        {renderLogos(logos)}
        {/* Cloned set so the slide past the last item lands on identical content. */}
        {renderLogos(logos, "clone-")}
      </BrandMarqueeTrack>
    </BrandMarquee>
  );
};

const HomePageBrandGallery = () => {
  const country = getCountry();
  if (country === AU_COUNTRY_CODE) return <Marquee logos={auLogos} />;
  if (country === UK_COUNTRY_CODE) return <Marquee logos={ukLogos} />;
  return null;
};

export default HomePageBrandGallery;
